import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { BookingPaymentOutcome } from "./BookingPaymentOutcome";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { startRazorpayPayment, getBookingIntent } from "../lib/razorpayCheckout";
import HomeCollectionSlotPicker from "./HomeCollectionSlotPicker";
import CouponInput from "./CouponInput";
import usePriceQuote, { MemberDiscountLine } from "../lib/usePriceQuote";
import { Plus, Check } from "lucide-react";
import { ReviewsBlock } from "./ReviewsWidget";
import { X, FlaskConical, Calendar, Clock, Home, Building2, User, Phone, Mail, CheckCircle2, CreditCard, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function DiagnosticBookingModal({ test, isOpen, onClose, onBookingSuccess }) {
  const { user } = useAuth();
  const [bookingType, setBookingType] = useState("Centre Visit"); // "Centre Visit" or "Home Sample Collection"
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  });
  const [timeSlot, setTimeSlot] = useState("08:00 AM - 09:00 AM");
  const [fastingConfirmed, setFastingConfirmed] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("Razorpay");

  // Patient details
  const [patientName, setPatientName] = useState(user?.name || "");
  const [patientPhone, setPatientPhone] = useState(user?.phone || "+91 98300 ");
  const [patientEmail, setPatientEmail] = useState(user?.email || "");
  const [patientAge, setPatientAge] = useState(user?.age || 38);
  const [patientGender, setPatientGender] = useState(user?.gender || "Male");
  const [address, setAddress] = useState(user?.address || "Salt Lake / Ballygunge, Kolkata");
  const [pincode, setPincode] = useState("");
  const [homeServiceable, setHomeServiceable] = useState(false);

  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [payState, setPayState] = useState(null); // "paid" | "failed" | "pending" | null (pay-at-centre)
  const [payMessage, setPayMessage] = useState("");
  const intentRef = useRef(null); const inFlight = useRef(false);
  const [error, setError] = useState("");
  const [addons, setAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [coupon, setCoupon] = useState(null);

  useEffect(() => {
    if (!isOpen || !test?.id) return;
    setSelectedAddons([]); setCoupon(null); setStep(1); setConfirmedBooking(null); setPayState(null); setPayMessage(""); setError(""); intentRef.current = null;
    axios.get(`${API}/diagnostics/${test.id}/addons`).then((r) => setAddons(r.data.addons || [])).catch(() => setAddons([]));
  }, [isOpen, test?.id]);

  const subtotal = (test?.price || 0) + selectedAddons.reduce((a, t) => a + (t.price || 0), 0);
  const quote = usePriceQuote({ amount: subtotal, bookingType: "diagnostic", email: patientEmail, enabled: isOpen && !!test });
  const memberDiscount = quote?.member_discount || 0;
  const totalAmount = Math.max(0, subtotal - memberDiscount - (coupon?.discount || 0));
  const toggleAddon = (t) => { setCoupon(null); setSelectedAddons(selectedAddons.some((x) => x.id === t.id) ? selectedAddons.filter((x) => x.id !== t.id) : [...selectedAddons, t]); };

  useEffect(() => {
    if (user) {
      if (!patientName) setPatientName(user.name);
      if (!patientEmail) setPatientEmail(user.email);
      if (user.phone && patientPhone.length <= 10) setPatientPhone(user.phone);
      if (user.address) setAddress(user.address);
    }
  }, [user]);

  if (!isOpen || !test) return null;

  const handleConfirmAndPay = async () => {
    if (!patientName || !patientPhone || !patientEmail) {
      setError("Please provide patient name, phone and email");
      return;
    }
    if (bookingType === "Home Sample Collection" && !address) {
      setError("Please provide home address for sample collection in Kolkata");
      return;
    }
    if (bookingType === "Home Sample Collection" && (!homeServiceable || !timeSlot)) {
      setError("Please enter a serviceable PIN code and pick an available home collection slot");
      return;
    }

    if (inFlight.current) return; // repeated taps while the request is processing
    if (paymentMethod === "Razorpay" && !user) { setError("Please sign in to pay online — your booking will be saved to My Health."); return; }
    inFlight.current = true; setIsSubmitting(true);
    setError("");
    try {
      const payload = {
        items: [test, ...selectedAddons].map((t) => ({ test_id: t.id, name: t.name, category: t.category, price: t.price })),
        booking_type: bookingType,
        date: selectedDate,
        time_slot: timeSlot,
        patient_name: patientName,
        patient_phone: patientPhone,
        patient_email: patientEmail,
        patient_age: Number(patientAge),
        patient_gender: patientGender,
        address: address,
        pincode: bookingType === "Home Sample Collection" ? pincode : null,
        fasting_confirmed: fastingConfirmed,
        payment_method: paymentMethod,
        total_amount: subtotal,
        coupon_code: coupon?.code || null
      };
      if (!intentRef.current) intentRef.current = await getBookingIntent(); // server-generated key: one booking per modal session
      const token = localStorage.getItem("sigmacare_token");
      const res = await axios.post(`${API}/bookings/diagnostic`, payload, { withCredentials: true, headers: { "Idempotency-Key": intentRef.current, ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      if (res.data.status !== "success") {
        setError("Failed to schedule test booking.");
        return;
      }
      const booking = res.data.booking;
      setConfirmedBooking(booking);

      if (paymentMethod === "Razorpay") {
        if (booking.payment_status === "Paid") { setPayState("paid"); setStep(3); if (onBookingSuccess) onBookingSuccess(booking); return; } // refreshed after an already-verified payment
        setPayState("pending"); setStep(3);
        await startRazorpayPayment({
          bookingId: booking.id,
          bookingType: "diagnostic",
          patient: { name: patientName, email: patientEmail, phone: patientPhone },
          centreName: "6SigmaCare Diagnostics",
          onSuccess: () => { setPayState("paid"); setPayMessage(""); if (onBookingSuccess) onBookingSuccess({ ...booking, payment_status: "Paid" }); },
          onFailure: (err) => { setPayState("failed"); setPayMessage(err?.message || "Payment failed."); },
        });
      } else {
        setPayState(null);
        setStep(3);
        if (onBookingSuccess) onBookingSuccess(booking);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to schedule test booking.");
    } finally {
      inFlight.current = false; setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div 
        data-testid="diagnostic-booking-modal"
        className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden"
      >
        {/* Header */}
        <div className="bg-[#0D3B2E] text-white p-6 relative">
          <button
            onClick={onClose}
            data-testid="diagnostic-modal-close-btn"
            className="absolute top-5 right-5 text-gray-300 hover:text-white p-1 rounded-full hover:bg-emerald-800"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-800 flex items-center justify-center text-amber-300 border border-emerald-600">
              <FlaskConical className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-[#07221A] px-2 py-0.2 rounded">
                  Diagnostic Booking
                </span>
              </div>
              <h3 className="font-serif-luxury text-xl font-bold text-white line-clamp-1">{test.name}</h3>
              <p className="text-xs text-emerald-200">{test.category} • TAT: {test.tat}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {error && (
            <div 
              data-testid="diagnostic-error-banner"
              className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl flex items-center gap-2"
            >
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
              {/sign in/i.test(error) && <Link to="/login" onClick={onClose} data-testid="diag-login-link" className="ml-auto underline font-bold whitespace-nowrap">Sign in</Link>}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              {/* Prep Instructions Callout */}
              <div className="p-3 bg-amber-50 rounded-2xl border border-amber-200 text-xs text-amber-900 space-y-1">
                <p className="font-bold flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600" /> Preparation & Guidelines:
                </p>
                <p className="text-[11px] text-amber-800">{test.preparation_instructions}</p>
                <p className="text-[11px] text-gray-600 font-medium">Sample: <b>{test.sample_type}</b> • Fasting: <b>{test.fasting_required ? "Yes (10-12 Hours)" : "Not Required"}</b></p>
              </div>
              <ReviewsBlock entityType="test" entityId={test.id} title="Patient experiences with this test" compact />

              {/* Booking Type */}
              <div>
                <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider mb-2">
                  Sample Collection / Test Location
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => { setBookingType("Centre Visit"); setTimeSlot("08:00 AM - 09:00 AM"); }}
                    data-testid="booking-type-centre"
                    className={`p-3 rounded-2xl border text-left flex items-start gap-2.5 transition-all ${bookingType === "Centre Visit" ? "border-[#0D3B2E] bg-[#EBF4F1] ring-1 ring-[#0D3B2E]" : "border-gray-200"}`}
                  >
                    <Building2 className="w-5 h-5 text-[#0D3B2E] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-gray-900">Centre Visit</p>
                      <p className="text-[11px] text-gray-500">Salt Lake / Ballygunge</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    disabled={!test.home_collection_available}
                    onClick={() => { setBookingType("Home Sample Collection"); setTimeSlot(""); }}
                    data-testid="booking-type-home"
                    className={`p-3 rounded-2xl border text-left flex items-start gap-2.5 transition-all ${!test.home_collection_available ? "opacity-50 cursor-not-allowed bg-gray-50" : bookingType === "Home Sample Collection" ? "border-[#0D3B2E] bg-[#EBF4F1] ring-1 ring-[#0D3B2E]" : "border-gray-200"}`}
                  >
                    <Home className="w-5 h-5 text-[#3B8271] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-gray-900">Home Collection</p>
                      <p className="text-[11px] text-gray-500">{test.home_collection_available ? "Doorstep in Kolkata" : "Not applicable (Centre only)"}</p>
                    </div>
                  </button>
                </div>
              </div>

              {addons.length > 0 && (
                <div data-testid="diag-addons">
                  <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider mb-2">Frequently added together</label>
                  <div className="flex flex-wrap gap-2">
                    {addons.map((t) => {
                      const on = selectedAddons.some((x) => x.id === t.id);
                      return (
                        <button key={t.id} type="button" onClick={() => toggleAddon(t)} data-testid={`diag-addon-${t.id}`}
                          className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border inline-flex items-center gap-1.5 transition-colors ${on ? "bg-[#0D3B2E] text-white border-[#0D3B2E]" : "bg-white text-gray-700 border-gray-200 hover:border-[#3B8271]"}`}>
                          {on ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />} {t.name.split("(")[0].trim()} · ₹{t.price}
                        </button>
                      );
                    })}
                  </div>
                  {selectedAddons.length > 0 && <p className="text-[11px] text-emerald-700 font-semibold mt-1.5" data-testid="diag-subtotal">Bundle subtotal ₹{subtotal} for {1 + selectedAddons.length} tests</p>}
                </div>
              )}

              {/* Date & Time */}
              {bookingType === "Home Sample Collection" ? (
                <HomeCollectionSlotPicker
                  idPrefix="diag-hc"
                  pincode={pincode}
                  onPincodeChange={setPincode}
                  date={selectedDate}
                  onDateChange={setSelectedDate}
                  slot={timeSlot}
                  onSlotChange={setTimeSlot}
                  onServiceability={setHomeServiceable}
                />
              ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    data-testid="diag-date-input"
                    className="w-full px-3 py-2.5 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Slot</label>
                  <select
                    value={timeSlot}
                    onChange={(e) => setTimeSlot(e.target.value)}
                    data-testid="diag-slot-select"
                    className="w-full px-3 py-2.5 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                  >
                    <option value="07:00 AM - 08:00 AM">07:00–08:00 AM (Fasting)</option>
                    <option value="08:00 AM - 09:00 AM">08:00–09:00 AM (Fasting)</option>
                    <option value="09:00 AM - 10:00 AM">09:00–10:00 AM</option>
                    <option value="11:00 AM - 12:00 PM">11:00–12:00 PM</option>
                    <option value="04:00 PM - 05:00 PM">04:00–05:00 PM</option>
                    <option value="06:00 PM - 07:00 PM">06:00–07:00 PM</option>
                  </select>
                </div>
              </div>
              )}

              <button
                type="button"
                disabled={bookingType === "Home Sample Collection" && (!homeServiceable || !timeSlot)}
                onClick={() => setStep(2)}
                data-testid="diag-next-step-btn"
                className="w-full py-3 bg-[#0D3B2E] hover:bg-[#165644] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-xl shadow-md transition-all"
              >
                Proceed to Patient Details (₹{totalAmount})
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Patient Full Name *</label>
                <input
                  type="text"
                  required
                  value={patientName}
                  onChange={(e) => setPatientName(e.target.value)}
                  data-testid="diag-patient-name"
                  className="w-full px-3 py-2.5 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Phone (WhatsApp) *</label>
                  <input
                    type="tel"
                    required
                    value={patientPhone}
                    onChange={(e) => setPatientPhone(e.target.value)}
                    data-testid="diag-patient-phone"
                    className="w-full px-3 py-2.5 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={patientEmail}
                    onChange={(e) => setPatientEmail(e.target.value)}
                    data-testid="diag-patient-email"
                    className="w-full px-3 py-2.5 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                  />
                </div>
              </div>

              {bookingType === "Home Sample Collection" && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Doorstep Address in Kolkata *</label>
                  <input
                    type="text"
                    required
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    data-testid="diag-address-input"
                    placeholder="Flat / House No, Street, Salt Lake / Kolkata Pin"
                    className="w-full px-3 py-2.5 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
                  />
                </div>
              )}

              {/* Payment selector */}
              <div>
                <label className="block text-xs font-bold text-gray-800 uppercase tracking-wider mb-1.5">
                  Payment Method
                </label>
                <div className="space-y-2">
                  <label className={`p-2.5 rounded-xl border flex items-center justify-between cursor-pointer text-xs ${paymentMethod === "Razorpay" ? "border-[#0D3B2E] bg-[#EBF4F1]" : "border-gray-200"}`}>
                    <span className="flex items-center gap-2">
                      <input type="radio" data-testid="diag-pay-option-razorpay" checked={paymentMethod === "Razorpay"} onChange={() => setPaymentMethod("Razorpay")} />
                      <b>Razorpay Online (UPI / Card)</b>
                    </span>
                    <CreditCard className="w-4 h-4 text-[#3B8271]" />
                  </label>
                  <label className={`p-2.5 rounded-xl border flex items-center justify-between cursor-pointer text-xs ${paymentMethod === "Pay at Centre" ? "border-[#0D3B2E] bg-[#EBF4F1]" : "border-gray-200"}`}>
                    <span className="flex items-center gap-2">
                      <input type="radio" data-testid="diag-pay-option-centre" checked={paymentMethod === "Pay at Centre"} onChange={() => setPaymentMethod("Pay at Centre")} />
                      <b>Pay at Centre Desk / Cash to Phlebotomist</b>
                    </span>
                    <Building2 className="w-4 h-4 text-gray-500" />
                  </label>
                </div>
              </div>

              <MemberDiscountLine quote={quote} testId="diag-member-line" />
              <CouponInput idPrefix="diag-coupon" amount={subtotal - memberDiscount} bookingType="diagnostic" email={patientEmail} applied={coupon} onApplied={setCoupon} />
              {coupon && <p className="text-[11px] text-emerald-800 font-semibold" data-testid="diag-coupon-line">Coupon {coupon.code} applied · −₹{coupon.discount} · payable ₹{totalAmount}</p>}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-1/3 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-xs font-semibold"
                >
                  ← Back
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleConfirmAndPay}
                  data-testid="diag-confirm-schedule-btn"
                  className="w-2/3 py-2.5 bg-[#0D3B2E] text-white rounded-xl text-xs font-semibold hover:bg-[#165644] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-wait"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4 text-amber-300" />}
                  {isSubmitting ? "Processing…" : paymentMethod === "Razorpay" ? `Proceed to Payment (₹${totalAmount})` : `Confirm Schedule (₹${totalAmount})`}
                </button>
              </div>
            </div>
          )}

          {step === 3 && confirmedBooking && (
            paymentMethod === "Razorpay" ? (
              <BookingPaymentOutcome kind="diagnostic" booking={confirmedBooking} state={payState || "pending"} message={payMessage} patient={{ name: patientName, email: patientEmail, phone: patientPhone }} centreName="6SigmaCare Diagnostics" onClose={onClose} testId="diag-outcome" />
            ) : (
            <div className="text-center py-4 space-y-4 animate-in zoom-in-95">
              <div className="w-16 h-16 bg-emerald-100 text-[#0D3B2E] rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <div>
                <h4 className="font-serif-luxury text-2xl font-bold text-[#0D3B2E]">Diagnostic Test Scheduled!</h4>
                <p className="text-xs text-gray-600 mt-1">
                  Lab Token ID: <b className="font-mono text-gray-900">{confirmedBooking.id}</b>
                </p>
              </div>

              <div className="p-4 bg-[#FBF9F4] rounded-2xl border border-emerald-200 text-left space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">{(confirmedBooking.items?.length || 1) > 1 ? "Tests:" : "Test:"}</span>
                  <span className="font-bold text-gray-900 text-right" data-testid="diag-confirmed-items">{(confirmedBooking.items || []).map((i) => i.name).join(" + ")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Date & Slot:</span>
                  <span className="font-semibold text-emerald-800">{confirmedBooking.date} • {confirmedBooking.time_slot}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Type:</span>
                  <span className="font-semibold">{confirmedBooking.booking_type}</span>
                </div>
                {confirmedBooking.phlebotomist_name && (
                  <div className="flex justify-between" data-testid="diag-confirmed-phlebotomist">
                    <span className="text-gray-500">Phlebotomist:</span>
                    <span className="font-semibold text-[#0D3B2E]">{confirmedBooking.phlebotomist_name} • PIN {confirmedBooking.pincode}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment:</span>
                  <span className="font-bold text-emerald-800">{confirmedBooking.payment_status} (₹{confirmedBooking.total_amount}{confirmedBooking.discount ? ` after ${confirmedBooking.coupon_code} −₹${confirmedBooking.discount}` : ""})</span>
                </div>
              </div>

              <button
                onClick={onClose}
                data-testid="diag-success-done-btn"
                className="w-full py-3 bg-[#0D3B2E] text-white text-xs font-semibold rounded-xl hover:bg-[#165644]"
              >
                Close & View in My Health
              </button>
            </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
